import { useEffect, useState } from 'react';
import { getPartner, subscribePartners } from '@/data/partnersRepo';
import type { PartnerType } from '@/types/types_partner';

/** Subscribes to the (currently in-memory) partner list. */
export function usePartners() {
  const [partners, setPartners] = useState<PartnerType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return subscribePartners((list) => {
      setPartners(list);
      setLoading(false);
    });
  }, []);

  return { partners, loading };
}

/** A single partner by id, derived from the subscribed list. */
export function usePartner(id: string | undefined) {
  const { partners, loading } = usePartners();
  const partner = id ? partners.find((p) => p.id === id) : undefined;
  return { partner, loading };
}

/**
 * A single partner read straight by id, for callers that may not list the
 * collection — the security rules let a partner read their own record and no
 * other, so `usePartner`'s subscription would fail for them. A read failure
 * leaves `partner` undefined rather than throwing.
 */
export function usePartnerById(id: string | undefined) {
  // Stamped with the id it describes, the way PartnerOnboardingGate does it:
  // "still loading" is then derived, and the record left by a previous id is
  // never read as this one without the effect resetting anything synchronously.
  const [result, setResult] = useState<{ id: string; partner?: PartnerType } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getPartner(id)
      .then((loaded) => {
        if (!cancelled) setResult({ id, partner: loaded });
      })
      .catch((e) => {
        console.error('Loading partner failed', e);
        if (!cancelled) setResult({ id });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const loaded = result?.id === id ? result : undefined;
  return { partner: loaded?.partner, loading: !!id && !loaded };
}
